import { takeEvery, put, call, select } from "redux-saga/effects";
import { message } from 'antd';
import { types } from "../reducers/authentication";
import { StorageManager } from "datastake-daf/src/helpers/StorageManager";
import { AuthenticationService } from "../../services";
import { UserService } from "../../services";
import ErrorFormat from '../../helpers/FormatErrors';
import { setUser } from "@sentry/react";
import i18next from 'i18next';
import { message as antMessage } from 'antd';
import moment from 'moment';
import localeEn from 'antd/es/date-picker/locale/en_US';
import localeFr from 'antd/es/date-picker/locale/fr_FR';
import { PROJECTS } from "../../helpers/CanAccess";
import { changeLanguage } from "../../helpers/Language";
import { getToken, isProxy } from 'datastake-daf/src/helpers/Token';
import { getAppFromModule, getApplication, getProject } from "../../services/BaseService";
import { createModule, getInterface, mapModulesFromApps, mapUser } from "../../helpers/user";
import AmpService from "../../services/AmpService";
import { tifRoles } from "../../constants/user";

const noRefreshPaths = [
  '/',
  '/confirm',
  '/app/confirm',
  '/register',
  '/password/reset',
];

function* check({ pathname, callback, mod }) {
  try {
    const token = yield getToken();
    const app = mod ? getAppFromModule(mod) : getApplication();

    if (token) {
      const { data: user } = yield call(() => UserService.check(app));
      if (user) {
        if (app === 'tif' && user?.apps?.tif && user?.apps?.tif?.role) {
          user.appRole = user.apps.tif.role;
          user.role = user.apps.tif.role;

          if (user.role.code === tifRoles.AMP) {
            const { data } = yield call(() => AmpService.getOne('user'));
            user.amp = data;
          }
        }

        mapModulesFromApps(user);

        setUser({
          id: user.id,
          email: user.email,
        });
        const userLang = navigator.language || navigator.userLanguage;
        const language = user.language ? user.language : (userLang || 'en');
        if (window.Userback && typeof window.Userback === 'object' && typeof window.Userback.setEmail === 'function') {
          window.Userback.setEmail(user.email);
          window.Userback.setName(`${user.firstName} ${user.lastName}`);
          window.Userback.setData({
            account_id: user.id,
            company_id: user.company.id,
            module: user.modules
          });
          window.Userback.widget_settings = { language };
          window.Userback.setWidgetSettings({
            language: language
          });
        }

        if (!isProxy()) {
          changeLanguage(language);
        }

        const selectedProject = getProject();
        if (selectedProject && user.projects && Array.isArray(user.projects)) {
          const proj = user.projects.find(p => p.projectId === selectedProject);

          if (proj) {
            yield put({
              type: types.CHANGE_SELECTED_PROJECT,
              value: selectedProject
            });
          } else {
            yield put({
              type: types.CHANGE_SELECTED_PROJECT,
              value: PROJECTS.myData
            });
          }
        } else {
          if (!selectedProject) {
            StorageManager.set('project', PROJECTS.myData);
          }
          yield put({
            type: types.CHANGE_SELECTED_PROJECT,
            value: PROJECTS.myData
          });
        }
        // TO-DO remove this
        yield put({ type: types.AUTHENTICATION_SUCCEDDED, value: user });
      }
      else yield put({ type: types.AUTHENTICATION_FAILED });
    } else yield put({ type: types.AUTHENTICATION_FAILED });

    if (typeof callback === 'function') {
      callback();
    }
  } catch (e) {
    console.log(e);
    StorageManager.clearOne("token");
    StorageManager.clearOne("previous");
    if (noRefreshPaths.includes(pathname)) {
      return;
    }
    yield put({ type: types.AUTHENTICATION_FAILED })
    yield put({ type: "CLEAR" });
  }
}

function* login({ value }) {
  const { username, password, firebaseToken, app, ...rest } = value;
  try {
    const {
      data: { user, token },
    } = yield call(() => AuthenticationService.login(username, password, firebaseToken, rest));
    if (token) {
      if (user?.apps?.tif && user?.apps?.tif?.role) {
        user.appRole = user.apps.tif.role;
        user.role = user.apps.tif.role;

        if (user.role.code === tifRoles.AMP) {
          const { data } = yield call(() => AmpService.getOne('user', token));
          user.amp = data;
        }
      }

      mapModulesFromApps(user);

      const userLang = navigator.language || navigator.userLanguage;
      const language = user.language ? user.language : (userLang || 'en');
      if (window.Userback && typeof window.Userback === 'object' && typeof window.Userback.setEmail === 'function') {
        window.Userback.setEmail(user.email);
        window.Userback.setName(`${user.firstName} ${user.lastName}`);
        window.Userback.setData({
          account_id: user.id,
          company_id: user.company_id,
          module: user.modules
        });
        window.Userback.setWidgetSettings({
          language: language
        });
      }

      StorageManager.set("token", token);
      changeLanguage(language);

      if (app) {
        window.location.href = `/app/${app}`;
        return;
      }

      // TO-DO remove this
      yield put({ type: types.AUTHENTICATION_SUCCEDDED, value: user });
    }
  } catch (e) {
    if (e.response) {
      const {
        response: {
          data: { errors, message },
        },
      } = e;
      if (errors && errors.length > 0) {
        const formattedErrors = {};
        errors.forEach((error) => {
          Object.assign(formattedErrors, {
            [error.property]: Object.values(error.constraints),
          });
        });
        yield put({ type: types.AUTHENTICATION_FAILED, value: formattedErrors });
        return;
      }
      yield put({ type: types.AUTHENTICATION_FAILED, value: { account: [message || "Wrong email or password"] } });
    } else {
      console.log(e);
    }

  }
}

function* register({ value }) {
  try {
    StorageManager.clearOne("token");
    StorageManager.clearOne("previous");
    const { callback, ...rest } = value;
    const res = yield call(() => AuthenticationService.register(rest));
    if (res && res.data && res.data.token) {
      if (typeof callback === 'function' && res.data.user.approvalStatus === 'approved') {
        callback();
        return;
      }

      StorageManager.set("token", res.data.token);
      yield put({ type: types.AUTHENTICATION_SUCCEDDED, value: res.data.user });
    }
  } catch (e) {
    console.log(e);
    const {
      response: {
        data: { errors, name, message },
      },
    } = e;
    let errorMsg = message;

    (errors || []).forEach((err) => {
      if (err.property === 'email') {
        errorMsg = i18next.t('Email already in use!');
      };
    });

    if (typeof message === 'string') {
      antMessage.error(errorMsg);
      yield put({ type: types.REGISTRATION_FAILED });
      return;
    }

    if (errors.length > 0) {
      const formattedErrors = {};
      errors.forEach((error) => {
        Object.assign(formattedErrors, {
          [error.property]: Object.values(error.constraints),
        });
      });
      yield put({ type: types.REGISTRATION_FAILED, value: formattedErrors });
      return;
    }
    yield put({ type: types.REGISTRATION_FAILED });
  }
  return true;
}

function* verify({ value }) {
  const formattedErrors = {};

  try {
    StorageManager.clearOne("token");
    StorageManager.clearOne("previous");
    yield put({ type: "CLEAR" });
    const {
      data: { user, token, message, language },
    } = yield call(() => AuthenticationService.verify(value));

    if (token) {
      yield put({ type: types.VERIFICATION_SUCCEDDED, value: mapUser(user) });
    } else {
      if (language) {
        changeLanguage(language);
      }
      if (message !== '') {
        formattedErrors.link = [message];
      }
      yield put({ type: types.VERIFICATION_FAILED, value: formattedErrors });
    }
  } catch (e) {
    if (e.response && e.response.data && e.response.data.errors) {
      const {
        response: {
          data: { errors },
        },
      } = e;
      if (errors.length > 0) {
        errors.forEach((error) => {
          Object.assign(formattedErrors, {
            [error.property]: Object.values(error.constraints),
          });
        });
        yield put({ type: types.VERIFICATION_FAILED, value: formattedErrors });
      } else {
        const message = e.response && e.response.data ? e.response.data.message : '';
        if (message !== '') {
          formattedErrors.link = [message];
        }
        yield put({ type: types.VERIFICATION_FAILED, value: formattedErrors });
      }
    } else {
      const message = e.response && e.response.data ? e.response.data.message : '';
      if (message !== '') {
        formattedErrors.link = [message];
      }
      yield put({ type: types.VERIFICATION_FAILED, value: formattedErrors });
    }
  }
}

function* resendVerification() {
  try {
    const { data } = yield call(() => AuthenticationService.resendVerification());
    if (data) {
      message.success('Verification sent!');
    }
  } catch (e) {
    const formattedErrors = {};
    if (e.response && e.response.data && e.response.data.errors) {
      const {
        response: {
          data: { errors },
        },
      } = e;
      if (errors.length > 0) {
        errors.forEach((error) => {
          Object.assign(formattedErrors, {
            [error.property]: Object.values(error.constraints),
          });
        });
        yield put({ type: types.RESEND_VERIFICATION_FAILED, value: formattedErrors });
      }
    }
  }
}

function* getRegistrationData({ value }) {
  try {
    if (StorageManager.get('token')) {
      const app = getApplication();
      StorageManager.clearOne("token");
      window.location.href = `/${app}/register?code=${value}`;
      return;
    }
    StorageManager.clearOne("previous");
    yield put({ type: "CLEAR" });
    const { data } = yield call(() => AuthenticationService.getRegistrationData(value));
    if (data) {
      yield put({
        type: types.REGISTRATION_DATA_SUCCEDDED,
        value: data,
      });
    }
  } catch (e) {
    const errors = ErrorFormat.format(e.response.data.errors);
    if (errors) {
      yield put({
        type: types.REGISTRATION_DATA_FAILED,
        value: errors,
      });
    }
  }
}

function* getRegistrationOptions({ value }) {
  try {
    const { data } = yield call(() => AuthenticationService.getRegistrationOptions(value));
    if (data) {
      yield put({
        type: types.REGISTRATION_OPTIONS_SUCCEDDED,
        value: data,
      });
    }
  } catch (e) {
    const errors = ErrorFormat.format(e.response.data.errors);
    if (errors) {
      yield put({
        type: types.REGISTRATION_OPTIONS_FAILED,
        value: errors,
      });
    }
  }
}

function* logout({ callback }) {
  try {
    // TODO: logout call
    setUser(null);
    StorageManager.clearOne("token");
    StorageManager.clearOne("project");
    StorageManager.clearOne("previous");
    if (typeof callback === 'function') {
      callback();
    }
    // const userLang = navigator.language || navigator.userLanguage;
    // i18next.changeLanguage(userLang || 'en');
    yield put({ type: types.LOGOUT_SUCCEDDED, value: true });
    yield put({ type: "CLEAR" });
  } catch (e) {
    console.log(e);
    yield put({ type: types.LOGOUT_FAILED });
  }
}

function* exitImpersonation() {
  try {
    const {
      data: { token }
    } = yield call(() => AuthenticationService.exitImpersonation());
    if (token) {
      StorageManager.set("token", token);
      StorageManager.clearOne("project");
      StorageManager.clearOne("previous");
      localStorage.removeItem('trade-type');
      localStorage.removeItem('trade-value');
      window.location = '/app/admin/users';
    }
  } catch (e) {
    console.log(e);
    yield put({ type: types.EXIT_IMPERSONATION_FAILED });
  }
}

function* resetPasswordRequest({ value }) {
  try {
    yield call(() => AuthenticationService.resetPasswordRequest(value));
    yield put({ type: types.RESET_PASSWORD_REQUEST_SUCCEDDED });
  } catch (e) {
    yield put({ type: types.RESET_PASSWORD_REQUEST_FAILED, value: e.response })
  }
}

function* resetPassword({ value }) {
  try {
    delete value.confirmPassword;
    yield call(() => AuthenticationService.resetPassword({
      plainPassword: value.password,
      verificationToken: value.passConfirmationToken,
    }));

    yield put({ type: types.RESET_PASSWORD_REQUEST_SUCCEDDED });
  } catch (e) {
    const msg = typeof e.response.data.message === 'string'
      ? e.response.data.message || 'Error Occurred'
      : 'Error Occurred';

    message.error({ content: msg });
    yield put({ type: types.RESET_PASSWORD_FAILED });
  }
}

function* updateLanguage({ value }) {
  try {
    const isUserLoggedIn = yield select((state) => state.authentication.authenticated);

    // check if the user is loged in. if so, change the user prefered language on the backend.
    // otherwise, just change the language locally. this is typically the login or sign up page
    // later on, the initial user language should be set from the language the user used on the sign up page
    if (isUserLoggedIn) {
      yield call(() => AuthenticationService.updateLanguage(value));
    }

    const language = value || 'en';

    changeLanguage(language);

    if (window.Userback && typeof window.Userback === 'object') {
      window.Userback.setWidgetSettings({
        language: language
      });
    }
    yield put({ type: types.UPDATE_LANGUAGE_SUCCEDDED, value });
  } catch (e) {
    message.error({ content: e.response.data.message });
    yield put({ type: types.UPDATE_LANGUAGE_FAILED });
  }
}

function* getInvitedUsers({ value: { callback } }) {
  try {
    const { user } = yield select((state) => state.authentication);
    const { data } = yield call(() => AuthenticationService.getInvitedUsers(user?.company?.id));
    if (data) {
      yield put({
        type: types.INVITED_USER_SUCCEDDED,
        value: [
          ...(data.users.map((u) => ({
            ...u,
            isAdmin: u.id === data.admin.id,
          }))),
          ...((data.pendingUsers || []).map((u, i) => ({
            ...u,
            id: u.invitationToken,
            status: 'pending',
            isAdmin: u.id === data.admin.id,
          }))),
        ],
      });
      if (typeof callback === 'function') {
        callback();
      }
    }
  } catch (e) {
    message.error({ content: e.response.data.message });
    yield put({ type: types.INVITED_USER_FAILED });
  }
}

function* inviteUser({ value, callback }) {
  try {
    const { data } = yield call(() => AuthenticationService.inviteUser(value));
    if (data) {
      callback(data);
      yield put({ type: types.INVITE_USER_SUCCEDDED });
    }
  } catch (e) {
    message.error({ content: e.response && e.response.data ? e.response.data.message : e.message });
    callback()
    yield put({ type: types.INVITE_USER_FAILED });
  }
}

function* getUserFromInvitation({ value, callback = () => { } }) {
  try {
    StorageManager.clearOne("token");
    StorageManager.clearOne("previous");
    yield put({ type: "CLEAR" });
    const { data } = yield call(() => AuthenticationService.getUserFromInvitation(value));
    if (data) {
      // StorageManager.set("token", data.token);
      if (data.user && data.user.language) {
        i18next.changeLanguage(data.user.language);
        const language = data.user.language;
        moment.locale(language, language === 'fr' ? localeFr : localeEn);
      }
      yield put({ type: types.USER_INVITATION_SUCCEDDED, value: { ...data.user, token: data.token } });
    }
    callback();
  } catch (e) {
    message.error({ content: i18next.t('This invitation link does not exist or is expired') });
    yield put({ type: types.USER_INVITATION_FAILED });
    callback();
  }
}

function* confirmInvitation({ value, callback, code = {} }) {
  try {
    const { user } = yield select((state) => state.authentication);
    const { data } = yield call(() => AuthenticationService.confirmInvitation(value, user.token, code));
    if (data) {
      callback(data);
      yield put({ type: types.CONFIRM_INVITE_SUCCEDDED });
    }
  } catch (e) {
    message.error({ content: e.response.data.message });
    yield put({ type: types.CONFIRM_INVITE_FAILED });
  }
}

function* demoLogin({ value, callback }) {
  try {
    const {
      data: { token, user },
    } = yield call(() => AuthenticationService.demoLogin(value));
    if (token) {
      StorageManager.set("token", token);
      const userLang = navigator.language || navigator.userLanguage;
      const language = user.language ? user.language : (userLang || 'en');
      StorageManager.set("token", token);
      changeLanguage(language);

      callback(true);
      yield put({
        type: types.DEMO_LOGIN_SUCCEDDED,
        value: user
      });
    }
  } catch (e) {
    callback(false);
    if (e.response) {
      const {
        response: {
          data: { errors },
        },
      } = e;
      if (errors && errors.length > 0) {
        const formattedErrors = {};
        errors.forEach((error) => {
          Object.assign(formattedErrors, {
            [error.property]: Object.values(error.constraints),
          });
        });
        yield put({ type: types.DEMO_LOGIN_FAILED, value: formattedErrors });
        return;
      }
      yield put({ type: types.DEMO_LOGIN_FAILED, value: { account: ["Wrong code"] } });
    } else {
      console.log(e);
    }
  }
}

function* demoSignup({ value, callback }) {
  try {
    const { data } = yield call(() => AuthenticationService.demoSignup(value));
    if (data) {
      callback(data);
      yield put({ type: types.DEMO_SIGNUP_SUCCEDDED });
    }
  } catch (e) {
    message.error({ content: e.response.data.message });
    yield put({ type: types.DEMO_SIGNUP_FAILED });
  }
}

function* removeUser({ value, callback }) {
  const { user, invitedUsers } = yield select((state) => state.authentication);

  try {
    yield call(() => AuthenticationService.removeSubUser(user?.company?.id, value));

    if (typeof callback === 'function') {
      callback({});
    }

    yield put({ type: types.INVITED_USER_SUCCEDDED, value: invitedUsers.filter((u) => u.id !== value) });
  } catch (err) {
    message.error(err?.response?.data?.message || err.message || 'Err');
    if (typeof callback === 'function') {
      callback({});
    }
  }
}

function* editUser({ value, callback }) {
  try {
    yield call(() => AuthenticationService.editSubUser(value));

    if (typeof callback === 'function') {
      callback({});
    }
  } catch (err) {
    message.error(err?.response?.data?.message || err.message || 'Err');
    // TO-DO remove this
    if (typeof callback === 'function') {
      callback({});
    }
  }
}

function* resendInvite({ value, callback }) {
  try {
    yield call(() => AuthenticationService.resendSubUserInvite(value));

    if (typeof callback === 'function') {
      callback({});
    }
  } catch (err) {
    message.error(i18next.t(err.msg || 'Error resending invite'));
  }
}

function* getOptions({ value }) {
  try {
    const { data } = yield call(() => AuthenticationService.getFormOptions({}));
    yield put({ type: types.SET_USER_OPTIONS, value: data });
  } catch (err) {
    console.log(err);
  }
}

export default function* init() {
  yield takeEvery(types.AUTHENTICATION_REQUESTED, login);
  yield takeEvery(types.LOGOUT_REQUESTED, logout);
  yield takeEvery(types.CHECK, check);
  yield takeEvery(types.AUTHENTICATION_REGISTER_REQUESTED, register);
  yield takeEvery(types.VERIFICATION_REQUEST, verify);
  yield takeEvery(types.REGISTRATION_DATA_REQUESTED, getRegistrationData);
  yield takeEvery(types.REGISTRATION_OPTIONS_REQUESTED, getRegistrationOptions);
  yield takeEvery(types.EXIT_IMPERSONTAION_REQUESTED, exitImpersonation);
  yield takeEvery(types.RESET_PASSWORD_REQUEST, resetPasswordRequest);
  yield takeEvery(types.RESET_PASSWORD, resetPassword);
  yield takeEvery(types.UPDATE_LANGUAGE_REQUESTED, updateLanguage);
  yield takeEvery(types.RESEND_VERIFICATION_REQUESTED, resendVerification);
  yield takeEvery(types.INVITED_USER_REQUESTED, getInvitedUsers);
  yield takeEvery(types.INVITE_USER_REQUESTED, inviteUser);
  yield takeEvery(types.REMOVE_USER_REQUESTED, removeUser);
  yield takeEvery(types.EDIT_USER_REQUESTED, editUser);
  yield takeEvery(types.RESEND_INVITE_USER_REQUESTED, resendInvite);
  yield takeEvery(types.CONFIRM_INVITE_REQUESTED, confirmInvitation);
  yield takeEvery(types.USER_INVITATION_REQUESTED, getUserFromInvitation);
  yield takeEvery(types.DEMO_LOGIN, demoLogin);
  yield takeEvery(types.GET_USER_OPTIONS, getOptions);
  yield takeEvery(types.DEMO_SIGNUP, demoSignup);
}
